/**
 * NAO checkout — actualizaciones de UI/estado del formulario:
 * totales, selección de método de pago, cantidad, ciudades y teléfono.
 * Recibe el contexto compartido (`ctx`) que arma el entry (nao-checkout.js).
 *
 * El envío NO se calcula aquí: sale de `computeShipping` en
 * @theme/nao-form-config, que es la única función que decide ese número en el
 * front y cuya lógica el backend replica antes de aceptar la orden.
 */

import { validateField } from '@theme/nao-checkout-validation';
import { computeShipping, freeShippingThreshold } from '@theme/nao-form-config';
import { hasAromas, aromaLines, aromaTotal, aromaSubtotal, describeAromas } from '@theme/nao-checkout-aromas';

const MAX_QUANTITY = 20;

// Cantidad actual del formulario, siempre saneada. Sin selector de cantidad
// (Kit de Inicio) el checkout es de una unidad, como hasta ahora.
export function currentQuantity(ctx) {

    // Con selector de aromas la cantidad es la suma de las lineas elegidas, y
    // puede ser 0: el cliente puede dejarlo todo a cero mientras decide. El
    // resto del checkout trata el 0 como "todavia no hay pedido" en vez de
    // corregirlo a 1, que le pondria en el carrito algo que no eligio.
    if (hasAromas(ctx)) {
        return Math.min(aromaTotal(ctx), MAX_QUANTITY);
    }

    const input = ctx.elements.qtyInput;

    if (!input) return 1;

    const parsed = parseInt(input.value, 10);

    if (!parsed || parsed < 1) return 1;

    return Math.min(parsed, MAX_QUANTITY);
}

/**
 * Recalcula subtotal, envío y total a partir de la cantidad y del método de
 * pago, y deja el costo de envío en el input oculto que viaja al backend.
 * Es el único punto que escribe cifras en pantalla.
 */
export function recalculate(ctx) {

    const { elements, PRODUCT_PRICE, formatter, config } = ctx;

    const quantity = currentQuantity(ctx);
    const shipping = computeShipping(config, { isCOD: ctx.isCOD, quantity });

    // Cada aroma podria tener su propio precio, asi que con selector de aromas
    // el subtotal se suma linea a linea en vez de multiplicar un precio unico.
    const subtotal = hasAromas(ctx) ? aromaSubtotal(ctx) : PRODUCT_PRICE * quantity;
    const total = subtotal + shipping;

    ctx.quantity = quantity;
    ctx.shippingCost = shipping;

    if (elements.qtyBadge) {
        elements.qtyBadge.textContent = String(quantity);
    }

    if (elements.summarySubtotal) {
        elements.summarySubtotal.textContent = formatter.format(subtotal);
    }

    // Precio por unidad, con el MISMO formateador que los totales: mezclar el
    // "$25.000,00" de Liquid con el "$25.000" del JS en la misma tarjeta hace
    // dudar de la cuenta.
    if (elements.unitPrice) {
        elements.unitPrice.textContent = formatter.format(PRODUCT_PRICE);
    }

    // Línea de envío siempre visible y siempre aparte del precio del producto.
    if (elements.summaryShipping) {
        elements.summaryShipping.textContent =
            shipping === 0 ? 'Gratis' : formatter.format(shipping);
    }

    if (elements.summaryTotal) {
        elements.summaryTotal.textContent = formatter.format(total);
    }

    // El input oculto es lo que lee buildPayload; mantenerlo sincronizado aquí
    // evita que el total mostrado y el enviado se separen.
    if (elements.shippingCostInput) {
        elements.shippingCostInput.value = String(shipping);
    }

    if (elements.qtyInput && String(quantity) !== elements.qtyInput.value) {
        elements.qtyInput.value = String(quantity);
    }

    updateMethodPrices(ctx, shipping);
    updateSubmitText(ctx, total);
    updateFreeNote(ctx, quantity);
    updateAromaState(ctx, quantity);
}

/**
 * Aviso de envio escalonado: es EL MISMO elemento cambiando de texto, nunca un
 * banner que aparece ni dos avisos apilados. Los dos textos los pone Liquid en
 * `data-*` para no repartir el copy entre plantilla y JS.
 */
function updateFreeNote(ctx, quantity) {

    const note = ctx.elements.freeNote;

    if (!note) return;

    const threshold = freeShippingThreshold(ctx.config);

    if (!threshold) return;

    const reached = quantity >= threshold;
    const text = reached ? note.dataset.reached : note.dataset.pending;

    if (text && note.textContent !== text) {
        note.textContent = text;
    }

    note.classList.toggle('nao-free-note--reached', reached);
}

/**
 * Estado del selector de aromas: el contador de unidades, el resumen de solo
 * lectura que el cliente confirma dentro del formulario, y el aviso de que
 * todavia no ha elegido nada.
 *
 * El boton de enviar se marca con `aria-disabled` y una clase, NO con la
 * propiedad `disabled`: esa la usa el propio submit para bloquearse mientras
 * espera al backend, y si ademas la tocaramos aqui un recalculo posterior lo
 * volveria a habilitar en mitad del envio.
 */
function updateAromaState(ctx, quantity) {

    if (!hasAromas(ctx)) return;

    const { aromaCount, aromaEmpty, variantTitle, submitBtn } = ctx.elements;
    const lines = aromaLines(ctx);

    if (aromaCount) {
        aromaCount.textContent = quantity === 1 ? '1 unidad' : `${quantity} unidades`;
    }

    if (variantTitle) {
        variantTitle.textContent = lines.length
            ? describeAromas(lines)
            : 'Sin aromas elegidos';
    }

    if (aromaEmpty) {
        aromaEmpty.hidden = quantity > 0;
    }

    if (submitBtn) {
        submitBtn.classList.toggle('nao-submit-button--blocked', quantity === 0);
        submitBtn.setAttribute('aria-disabled', quantity === 0 ? 'true' : 'false');
    }
}

// En las reglas por cantidad el envío es el mismo para los dos métodos de pago,
// así que ambas tarjetas muestran el valor vigente. En las reglas por método de
// pago el texto ya lo imprime Liquid y no se toca.
function updateMethodPrices(ctx, shipping) {

    if (freeShippingThreshold(ctx.config) === null) return;

    // "Envío gratis", no "GRATIS" a secas: junto al nombre del método de pago,
    // un "GRATIS" suelto parece decir que el método no cuesta nada.
    const label = shipping === 0
        ? 'Envío gratis'
        : 'Envío ' + ctx.formatter.format(shipping);

    ctx.elements.methodPrices.forEach(element => {
        element.textContent = label;
    });
}

function updateSubmitText(ctx, total) {

    const { elements, formatter, config } = ctx;

    if (elements.btnMainText) {

        elements.btnMainText.textContent = ctx.isCOD
            ? `CONFIRMAR PEDIDO CONTRA ENTREGA POR ${formatter.format(total)}`
            : `IR AL PAGO SEGURO POR ${formatter.format(total)}`;
    }

    if (!elements.btnSubText) return;

    if (config.regaloGotero) {

        elements.btnSubText.textContent = ctx.isCOD
            ? 'PAGO SEGURO AL RECIBIR TU PAQUETE'
            : 'ENVÍO GRATIS + GOTERO DE REGALO';

        return;
    }

    elements.btnSubText.textContent = ctx.isCOD
        ? 'Pagas al recibir el pedido'
        : 'Pago protegido';
}

export function applySelection(ctx, method, cost, isCOD) {

    const { elements } = ctx;

    elements.shippingMethods.forEach(item => {
        item.classList.remove('nao-selected');
        // Patrón radiogroup: solo el radio marcado queda en el tab order.
        item.setAttribute('aria-checked', 'false');
        item.setAttribute('tabindex', '-1');
    });

    method.classList.add('nao-selected');
    method.setAttribute('aria-checked', 'true');
    method.setAttribute('tabindex', '0');

    ctx.isCOD = !!isCOD;

    if (elements.lossAlert) {
        elements.lossAlert.style.display = isCOD ? 'block' : 'none';
    }

    if (elements.submitBtn) {

        if (isCOD) {
            elements.submitBtn.classList.remove('nao-vibrant');
            elements.submitBtn.classList.add('nao-neutral-btn');
        } else {
            elements.submitBtn.classList.add('nao-vibrant');
            elements.submitBtn.classList.remove('nao-neutral-btn');
        }
    }

    recalculate(ctx);

    ctx.saveToLocalStorage();
}

export function populateCities(ctx, department) {

    const { elements, colombiaData } = ctx;

    if (!elements.citySelect) return;

    elements.citySelect.innerHTML =
        '<option value="" disabled selected>CIUDAD</option>';

    const cities =
        colombiaData[department];

    if (!cities) {

        elements.citySelect.disabled = true;

        return;
    }

    elements.citySelect.disabled = false;

    cities
        .sort()
        .forEach(city => {

            const option =
                document.createElement('option');

            option.value = city;
            option.textContent = city;

            elements.citySelect.appendChild(option);
        });
}

export function handlePhoneSync(ctx) {

    const {
        whatsappCheckbox,
        whatsappPhone,
        shippingPhone
    } = ctx.elements;

    if (
        !whatsappCheckbox ||
        !whatsappPhone ||
        !shippingPhone
    ) {
        return;
    }

    if (whatsappCheckbox.checked) {

        whatsappPhone.value =
            shippingPhone.value;

        whatsappPhone.readOnly = true;

        whatsappPhone.style.backgroundColor =
            '#f3f4f6';

        if (whatsappPhone.value) {
            validateField(whatsappPhone);
        }

    } else {

        whatsappPhone.readOnly = false;

        whatsappPhone.style.backgroundColor =
            '#fff';
    }
}
